import roboto from "gatsby-plugin-theme-ui";
import { merge } from "theme-ui";
import { hexToRgba } from "./utils";

const fontFaces = `
@font-face {
  font-family: 'Nunito Sans';
  font-weight: 200 1000;
  font-style: normal;
  font-display: swap;
  src: url('/fonts/NunitoSans-VariableFont_YTLC,opsz,wdth,wght.ttf') format('truetype');
  font-optical-sizing: auto;
  font-variation-settings: "wdth" 100, "YTLC" 500;
}
`;

// --- base "severe" color scheme (current dark theme) ---
const severeDark = {
  primary: "#E9C16C",
  // primaryHover: hexToRgba("#E9C16C", 0.8),
  // outlineButtonHover: hexToRgba("#000000", 0.2),
  surfaceTint: "#E9C16C",
  onPrimary: "#402D00",
  primaryContainer: "#5B4300",
  // primaryContainerHover: hexToRgba("#5B4300", 0.8),
  onPrimaryContainer: "#FFDF9E",
  secondary: "#D8C4A0",
  onSecondary: "#64615A",
  secondaryContainer: "#52452A",
  onSecondaryContainer: "#F5E0BB",
  tertiary: "#B0CFA9",
  onTertiary: "#1D361C",
  tertiaryContainer: "#334D30",
  // tertiaryContainerHover: hexToRgba("#334D30", 0.8),
  onTertiaryContainer: "#CCEBC4",
  error: "#FFB4AB",
  // errorHover: hexToRgba("#FFB4AB", 0.8),
  onError: "#690005",
  errorContainer: "#93000A",
  // errorContainerHover: hexToRgba("#93000A", 0.8),
  onErrorContainer: "#FFDAD6",
  background: "#17130B",
  onBackground: "#EBE1D4",
  surface: "#17130B",
  onSurface: "#EBE1D4",
  surfaceVariant: "#4D4639",
  onSurfaceVariant: "#D0C5B4",
  outline: "#998F80",
  outlineVariant: "#4D4639",
  shadow: "#000000",
  scrim: "#000000",
  inverseSurface: "#EBE1D4",
  inverseOnSurface: "#353027",
  inversePrimary: "#785A0B",
  primaryFixed: "#FFDF9E",
  onPrimaryFixed: "#261A00",
  primaryFixedDim: "#E9C16C",
  onPrimaryFixedVariant: "#5B4300",
  secondaryFixed: "#F5E0BB",
  onSecondaryFixed: "#241A04",
  secondaryFixedDim: "#D8C4A0",
  onSecondaryFixedVariant: "#52452A",
  tertiaryFixed: "#CCEBC4",
  onTertiaryFixed: "#072008",
  tertiaryFixedDim: "#B0CFA9",
  onTertiaryFixedVariant: "#334D30",
  surfaceDim: "#17130B",
  surfaceBright: "#3E382F",
  surfaceContainerLowest: "#110E07",
  surfaceContainerLow: "#1F1B13",
  surfaceContainer: "#231F17",
  surfaceContainerHigh: "#2E2921",
  surfaceContainerHighest: "#39342B",
  text: "onSurface",
};

// --- severe LIGHT mode (from material export: schemes.light) ---
const severeLight = {
  primary: "#785A0B",
  // primaryHover: hexToRgba("#785A0B", 0.8),
  // outlineButtonHover: hexToRgba("#000000", 0.06),
  surfaceTint: "#785A0B",
  onPrimary: "#FFFFFF",
  primaryContainer: "#FFDF9E",
  // primaryContainerHover: hexToRgba("#FFDF9E", 0.8),
  onPrimaryContainer: "#251A00",

  secondary: "#6C5C3A",
  onSecondary: "#FFFFFF",
  secondaryContainer: "#F5E0BB",
  onSecondaryContainer: "#241A04",

  tertiary: "#466343",
  onTertiary: "#FFFFFF",
  tertiaryContainer: "#CCEBC4",
  tertiaryContainerHover: hexToRgba("#CCEBC4", 0.8),
  onTertiaryContainer: "#072008",

  error: "#BA1A1A",
  errorHover: hexToRgba("#BA1A1A", 0.8),
  onError: "#FFFFFF",
  errorContainer: "#FFDAD6",
  errorContainerHover: hexToRgba("#FFDAD6", 0.8),
  onErrorContainer: "#410002",

  background: "#FFF8F0",
  onBackground: "#201B10",
  surface: "#FFF8F0",
  onSurface: "#201B10",

  surfaceVariant: "#EAE0CF",
  onSurfaceVariant: "#4D4639",

  outline: "#7F7667",
  outlineVariant: "#D0C5B4",

  shadow: "#000000",
  scrim: "#000000",

  inverseSurface: "#353027",
  inverseOnSurface: "#FBEDDE",
  inversePrimary: "#E9C16C",

  primaryFixed: "#FFDF9E",
  onPrimaryFixed: "#251A00",
  primaryFixedDim: "#E9C16C",
  onPrimaryFixedVariant: "#5B4300",

  secondaryFixed: "#F5E0BB",
  onSecondaryFixed: "#241A04",
  secondaryFixedDim: "#D8C4A0",
  onSecondaryFixedVariant: "#52452A",

  tertiaryFixed: "#CCEBC4",
  onTertiaryFixed: "#072008",
  tertiaryFixedDim: "#B0CFA9",
  onTertiaryFixedVariant: "#334D30",

  surfaceDim: "#E1D7C8",
  surfaceBright: "#FFF8F0",
  surfaceContainerLowest: "#FFFFFF",
  surfaceContainerLow: "#FBEDDE",
  surfaceContainer: "#F5E7D9",
  surfaceContainerHigh: "#EFE1D4",
  surfaceContainerHighest: "#E8DBCD",

  text: "onSurface",
};

// --- severe DARK HIGH‑CONTRAST ---
const severeDarkHighContrast = {
  ...severeDark,
  // strong foregrounds on dark
  primary: "#FFF1C1",
  onPrimary: "#221400",
  primaryContainer: "#E9C16C",
  onPrimaryContainer: "#140C00",

  secondary: "#FDE9C9",
  onSecondary: "#1A1000",
  secondaryContainer: "#D8C4A0",
  onSecondaryContainer: "#130C00",

  tertiary: "#E4F2DF",
  onTertiary: "#001405",
  tertiaryContainer: "#B0CFA9",
  onTertiaryContainer: "#001003",

  onSurface: "#FFF8F0",
  onSurfaceVariant: "#F2E7D6",
  outline: "#F2E7D6",

  error: "#FFEDEA",
  onError: "#2B0001",
  errorContainer: "#FFB4AB",
  onErrorContainer: "#2B0001",
};

// --- severe LIGHT HIGH‑CONTRAST ---
const severeLightHighContrast = {
  ...severeLight,
  // darker text and containers for max readability
  primary: "#3B2600",
  onPrimary: "#FFFFFF",
  primaryContainer: "#5B4300",
  onPrimaryContainer: "#FFF1C1",

  secondary: "#3A2C10",
  onSecondary: "#FFFFFF",
  secondaryContainer: "#52452A",
  onSecondaryContainer: "#FDE9C9",

  tertiary: "#21371F",
  onTertiary: "#FFFFFF",
  tertiaryContainer: "#334D30",
  onTertiaryContainer: "#E4F2DF",

  onSurface: "#181208",
  onSurfaceVariant: "#3A3327",
  outline: "#3A3327",

  error: "#4E0004",
  onError: "#FFFFFF",
  errorContainer: "#730008",
  onErrorContainer: "#FFEDEA",
};

// --- PALETTES for "severe" (for future theming / tokens) ---
const severePalettes = {
  primary: {
    0: "#000000",
    10: "#261A00",
    20: "#3D2900",
    30: "#554000",
    40: "#705800",
    50: "#8C710E",
    60: "#A88A2A",
    70: "#C4A545",
    80: "#E1C061",
    90: "#FFDF9E",
    95: "#FFF1C9",
    99: "#FFFBFF",
    100: "#FFFFFF",
  },
  secondary: {
    0: "#000000",
    10: "#241A04",
    20: "#3A2C10",
    30: "#52452A",
    40: "#6C5C3A",
    50: "#877650",
    60: "#A38F69",
    70: "#C0AA83",
    80: "#DCC59E",
    90: "#F5E0BB",
    95: "#FFF1D1",
    99: "#FFFBFF",
    100: "#FFFFFF",
  },
  tertiary: {
    0: "#000000",
    10: "#072008",
    20: "#1A3419",
    30: "#334D30",
    40: "#4B6647",
    50: "#64805F",
    60: "#7F9B79",
    70: "#99B692",
    80: "#B4D1AD",
    90: "#CCEBC4",
    95: "#DBFADA",
    99: "#F6FFF2",
    100: "#FFFFFF",
  },
  neutral: {
    0: "#000000",
    4: "#090604",
    6: "#0D0905",
    10: "#17130B",
    20: "#2C261A",
    30: "#42392A",
    40: "#5A503F",
    50: "#746858",
    60: "#8E8271",
    70: "#A9A08E",
    80: "#C5BAA8",
    90: "#E1D7C8",
    95: "#F3E8D8",
    99: "#FFFBFF",
    100: "#FFFFFF",
  },
  neutralVariant: {
    0: "#000000",
    10: "#231D12",
    20: "#393224",
    30: "#4D4639",
    40: "#655D4F",
    50: "#7F7667",
    60: "#998F80",
    70: "#B4A895",
    80: "#D0C5B4",
    90: "#ECE0CF",
    95: "#FAEEDD",
    99: "#FFFBFF",
    100: "#FFFFFF",
  },
  error: {
    0: "#000000",
    10: "#410002",
    20: "#690005",
    30: "#93000A",
    40: "#BA1A1A",
    50: "#DE3730",
    60: "#FF5449",
    70: "#FF897D",
    80: "#FFB4AB",
    90: "#FFDAD6",
    95: "#FFEDEA",
    99: "#FFFBFF",
    100: "#FFFFFF",
  },
};

// --- WILDFIRE schemes  ---

const wildfireLight = {
  primary: "#904B3B",
  surfaceTint: "#904B3B",
  onPrimary: "#FFFFFF",
  primaryContainer: "#FFDAD2",
  onPrimaryContainer: "#733426",

  secondary: "#77574F",
  onSecondary: "#FFFFFF",
  secondaryContainer: "#FFDAD2",
  onSecondaryContainer: "#5D3F39",

  tertiary: "#6D5D2E",
  onTertiary: "#FFFFFF",
  tertiaryContainer: "#F8E1A6",
  onTertiaryContainer: "#544519",

  error: "#BA1A1A",
  onError: "#FFFFFF",
  errorContainer: "#FFDAD6",
  onErrorContainer: "#93000A",

  background: "#FFF8F6",
  onBackground: "#231917",
  surface: "#FFF8F6",
  onSurface: "#231917",

  surfaceVariant: "#F5DDD8",
  onSurfaceVariant: "#534340",

  outline: "#85736F",
  outlineVariant: "#D8C2BD",

  shadow: "#000000",
  scrim: "#000000",

  inverseSurface: "#392E2C",
  inverseOnSurface: "#FFEDE9",
  inversePrimary: "#FFB4A3",

  primaryFixed: "#FFDAD2",
  onPrimaryFixed: "#3A0A02",
  primaryFixedDim: "#FFB4A3",
  onPrimaryFixedVariant: "#733426",

  secondaryFixed: "#FFDAD2",
  onSecondaryFixed: "#2C1510",
  secondaryFixedDim: "#E7BDB4",
  onSecondaryFixedVariant: "#5D3F39",

  tertiaryFixed: "#F8E1A6",
  onTertiaryFixed: "#241A00",
  tertiaryFixedDim: "#DBC58C",
  onTertiaryFixedVariant: "#544519",

  surfaceDim: "#E8D6D2",
  surfaceBright: "#FFF8F6",
  surfaceContainerLowest: "#FFFFFF",
  surfaceContainerLow: "#FFF0ED",
  surfaceContainer: "#FCEAE6",
  surfaceContainerHigh: "#F7E4E0",
  surfaceContainerHighest: "#F1DFDB",

  primaryHover: hexToRgba("#904B3B", 0.8),
  tertiaryContainerHover: hexToRgba("#F8E1A6", 0.8),
  outlineButtonHover: hexToRgba("#ffffff", 0.3),
  errorContainerHover: hexToRgba("#FFDAD6", 0.8),

  text: "onSurface",
};

const wildfireLightMediumContrast = {
  primary: "#5D2417",
  surfaceTint: "#904B3B",
  onPrimary: "#FFFFFF",
  primaryContainer: "#A15949",
  onPrimaryContainer: "#FFFFFF",

  secondary: "#4B2F29",
  onSecondary: "#FFFFFF",
  secondaryContainer: "#87655D",
  onSecondaryContainer: "#FFFFFF",

  tertiary: "#423509",
  onTertiary: "#FFFFFF",
  tertiaryContainer: "#7D6B3B",
  onTertiaryContainer: "#FFFFFF",

  error: "#740006",
  onError: "#FFFFFF",
  errorContainer: "#CF2C27",
  onErrorContainer: "#FFFFFF",

  background: "#FFF8F6",
  onBackground: "#231917",
  surface: "#FFF8F6",
  onSurface: "#180F0D",

  surfaceVariant: "#F5DDD8",
  onSurfaceVariant: "#413330",

  outline: "#5F4F4B",
  outlineVariant: "#7B6965",

  shadow: "#000000",
  scrim: "#000000",

  inverseSurface: "#392E2C",
  inverseOnSurface: "#FFEDE9",
  inversePrimary: "#FFB4A3",

  primaryFixed: "#A15949",
  onPrimaryFixed: "#FFFFFF",
  primaryFixedDim: "#844233",
  onPrimaryFixedVariant: "#FFFFFF",

  secondaryFixed: "#87655D",
  onSecondaryFixed: "#FFFFFF",
  secondaryFixedDim: "#6D4D46",
  onSecondaryFixedVariant: "#FFFFFF",

  tertiaryFixed: "#7D6B3B",
  onTertiaryFixed: "#FFFFFF",
  tertiaryFixedDim: "#635326",
  onTertiaryFixedVariant: "#FFFFFF",

  surfaceDim: "#D4C3BF",
  surfaceBright: "#FFF8F6",
  surfaceContainerLowest: "#FFFFFF",
  surfaceContainerLow: "#FFF0ED",
  surfaceContainer: "#F7E4E0",
  surfaceContainerHigh: "#EBD9D5",
  surfaceContainerHighest: "#DFCECA",

  primaryHover: hexToRgba("#A15949", 0.8),
  tertiaryContainerHover: hexToRgba("#7D6B3B", 0.8),
  outlineButtonHover: hexToRgba("#ffffff", 0.3),
  errorContainerHover: hexToRgba("#CF2C27", 0.8),

  text: "onSurface",
};

const wildfireLightHighContrast = {
  primary: "#501B0E",
  surfaceTint: "#904B3B",
  onPrimary: "#FFFFFF",
  primaryContainer: "#753728",
  onPrimaryContainer: "#FFFFFF",

  secondary: "#3F251F",
  onSecondary: "#FFFFFF",
  secondaryContainer: "#60423B",
  onSecondaryContainer: "#FFFFFF",

  tertiary: "#372B02",
  onTertiary: "#FFFFFF",
  tertiaryContainer: "#57481B",
  onTertiaryContainer: "#FFFFFF",

  error: "#600004",
  onError: "#FFFFFF",
  errorContainer: "#98000A",
  onErrorContainer: "#FFFFFF",

  background: "#FFF8F6",
  onBackground: "#231917",
  surface: "#FFF8F6",
  onSurface: "#000000",

  surfaceVariant: "#F5DDD8",
  onSurfaceVariant: "#000000",

  outline: "#372926",
  outlineVariant: "#554642",

  shadow: "#000000",
  scrim: "#000000",

  inverseSurface: "#392E2C",
  inverseOnSurface: "#FFFFFF",
  inversePrimary: "#FFB4A3",

  primaryFixed: "#753728",
  onPrimaryFixed: "#FFFFFF",
  primaryFixedDim: "#592114",
  onPrimaryFixedVariant: "#FFFFFF",

  secondaryFixed: "#60423B",
  onSecondaryFixed: "#FFFFFF",
  secondaryFixedDim: "#472C26",
  onSecondaryFixedVariant: "#FFFFFF",

  tertiaryFixed: "#57481B",
  onTertiaryFixed: "#FFFFFF",
  tertiaryFixedDim: "#3E3106",
  onTertiaryFixedVariant: "#FFFFFF",

  surfaceDim: "#C6B5B2",
  surfaceBright: "#FFF8F6",
  surfaceContainerLowest: "#FFFFFF",
  surfaceContainerLow: "#FFEDE9",
  surfaceContainer: "#F1DFDB",
  surfaceContainerHigh: "#E2D1CD",
  surfaceContainerHighest: "#D4C3BF",

  primaryHover: hexToRgba("#753728", 0.8),
  tertiaryContainerHover: hexToRgba("#57481B", 0.8),
  outlineButtonHover: hexToRgba("#ffffff", 0.3),
  errorContainerHover: hexToRgba("#98000A", 0.8),

  text: "onSurface",
};

const wildfireDark = {
  primary: "#FFB4A3",
  surfaceTint: "#FFB4A3",
  onPrimary: "#561F12",
  primaryContainer: "#733426",
  onPrimaryContainer: "#FFDAD2",

  secondary: "#E7BDB4",
  onSecondary: "#442A23",
  secondaryContainer: "#5D3F39",
  onSecondaryContainer: "#FFDAD2",

  tertiary: "#DBC58C",
  onTertiary: "#3C2F04",
  tertiaryContainer: "#544519",
  onTertiaryContainer: "#F8E1A6",

  error: "#FFB4AB",
  onError: "#690005",
  errorContainer: "#93000A",
  onErrorContainer: "#FFDAD6",

  background: "#1A110F",
  onBackground: "#F1DFDB",
  surface: "#1A110F",
  onSurface: "#F1DFDB",

  surfaceVariant: "#534340",
  onSurfaceVariant: "#D8C2BD",

  outline: "#A08C88",
  outlineVariant: "#534340",

  shadow: "#000000",
  scrim: "#000000",

  inverseSurface: "#F1DFDB",
  inverseOnSurface: "#392E2C",
  inversePrimary: "#904B3B",

  primaryFixed: "#FFDAD2",
  onPrimaryFixed: "#3A0A02",
  primaryFixedDim: "#FFB4A3",
  onPrimaryFixedVariant: "#733426",

  secondaryFixed: "#FFDAD2",
  onSecondaryFixed: "#2C1510",
  secondaryFixedDim: "#E7BDB4",
  onSecondaryFixedVariant: "#5D3F39",

  tertiaryFixed: "#F8E1A6",
  onTertiaryFixed: "#241A00",
  tertiaryFixedDim: "#DBC58C",
  onTertiaryFixedVariant: "#544519",

  surfaceDim: "#1A110F",
  surfaceBright: "#423734",
  surfaceContainerLowest: "#140C0A",
  surfaceContainerLow: "#231917",
  surfaceContainer: "#271D1B",
  surfaceContainerHigh: "#322825",
  surfaceContainerHighest: "#3D3230",

  primaryHover: hexToRgba("#FFB4A3", 0.8),
  tertiaryContainerHover: hexToRgba("#544519", 0.8),
  outlineButtonHover: hexToRgba("#000000", 0.2),
  errorContainerHover: hexToRgba("#93000A", 0.8),

  text: "onSurface",
};

const wildfireDarkMediumContrast = {
  primary: "#FFD2C8",
  surfaceTint: "#FFB4A3",
  onPrimary: "#481409",
  primaryContainer: "#CB7C69",
  onPrimaryContainer: "#000000",

  secondary: "#FED3C9",
  onSecondary: "#381F19",
  secondaryContainer: "#AE8880",
  onSecondaryContainer: "#000000",

  tertiary: "#F1DAA0",
  onTertiary: "#302400",
  tertiaryContainer: "#A28F5B",
  onTertiaryContainer: "#000000",

  error: "#FFD2CC",
  onError: "#540003",
  errorContainer: "#FF5449",
  onErrorContainer: "#000000",

  background: "#1A110F",
  onBackground: "#F1DFDB",
  surface: "#1A110F",
  onSurface: "#FFFFFF",

  surfaceVariant: "#534340",
  onSurfaceVariant: "#EED7D2",

  outline: "#C2ADA9",
  outlineVariant: "#A08C88",

  shadow: "#000000",
  scrim: "#000000",

  inverseSurface: "#F1DFDB",
  inverseOnSurface: "#322825",
  inversePrimary: "#743527",

  primaryFixed: "#FFDAD2",
  onPrimaryFixed: "#2A0300",
  primaryFixedDim: "#FFB4A3",
  onPrimaryFixedVariant: "#5D2417",

  secondaryFixed: "#FFDAD2",
  onSecondaryFixed: "#200B07",
  secondaryFixedDim: "#E7BDB4",
  onSecondaryFixedVariant: "#4B2F29",

  tertiaryFixed: "#F8E1A6",
  onTertiaryFixed: "#171000",
  tertiaryFixedDim: "#DBC58C",
  onTertiaryFixedVariant: "#423509",

  surfaceDim: "#1A110F",
  surfaceBright: "#4E423F",
  surfaceContainerLowest: "#0D0605",
  surfaceContainerLow: "#251B19",
  surfaceContainer: "#302623",
  surfaceContainerHigh: "#3B302E",
  surfaceContainerHighest: "#463B39",

  primaryHover: hexToRgba("#FFD2C8", 0.8),
  tertiaryContainerHover: hexToRgba("#A28F5B", 0.8),
  outlineButtonHover: hexToRgba("#000000", 0.2),
  errorContainerHover: hexToRgba("#FF5449", 0.8),

  text: "onSurface",
};

const wildfireDarkHighContrast = {
  primary: "#FFECE8",
  surfaceTint: "#FFB4A3",
  onPrimary: "#000000",
  primaryContainer: "#FFAF9C",
  onPrimaryContainer: "#200200",

  secondary: "#FFECE8",
  onSecondary: "#000000",
  secondaryContainer: "#E3B9B0",
  onSecondaryContainer: "#190603",

  tertiary: "#FFEFCA",
  onTertiary: "#000000",
  tertiaryContainer: "#D7C189",
  onTertiaryContainer: "#100A00",

  error: "#FFECE9",
  onError: "#000000",
  errorContainer: "#FFAEA4",
  onErrorContainer: "#220001",

  background: "#1A110F",
  onBackground: "#F1DFDB",
  surface: "#1A110F",
  onSurface: "#FFFFFF",

  surfaceVariant: "#534340",
  onSurfaceVariant: "#FFFFFF",

  outline: "#FFECE8",
  outlineVariant: "#D4BEB9",

  shadow: "#000000",
  scrim: "#000000",

  inverseSurface: "#F1DFDB",
  inverseOnSurface: "#000000",
  inversePrimary: "#743527",

  primaryFixed: "#FFDAD2",
  onPrimaryFixed: "#000000",
  primaryFixedDim: "#FFB4A3",
  onPrimaryFixedVariant: "#2A0300",

  secondaryFixed: "#FFDAD2",
  onSecondaryFixed: "#000000",
  secondaryFixedDim: "#E7BDB4",
  onSecondaryFixedVariant: "#200B07",

  tertiaryFixed: "#F8E1A6",
  onTertiaryFixed: "#000000",
  tertiaryFixedDim: "#DBC58C",
  onTertiaryFixedVariant: "#171000",

  surfaceDim: "#1A110F",
  surfaceBright: "#5A4D4B",
  surfaceContainerLowest: "#000000",
  surfaceContainerLow: "#271D1B",
  surfaceContainer: "#392E2C",
  surfaceContainerHigh: "#443936",
  surfaceContainerHighest: "#504441",

  primaryHover: hexToRgba("#FFECE8", 0.8),
  tertiaryContainerHover: hexToRgba("#D7C189", 0.8),
  outlineButtonHover: hexToRgba("#000000", 0.2),
  errorContainerHover: hexToRgba("#FFAEA4", 0.8),

  text: "onSurface",
};

const wildfirePalettes = {
  primary: {
    0: "#000000",
    5: "#2A0300",
    10: "#3A0A02",
    15: "#481408",
    20: "#561F12",
    25: "#64291C",
    30: "#733426",
    35: "#813F30",
    40: "#904B3B",
    50: "#AE6351",
    60: "#CC7B69",
    70: "#EB9581",
    80: "#FFB4A3",
    90: "#FFDAD2",
    95: "#FFEDE9",
    98: "#FFF8F6",
    99: "#FFFBFF",
    100: "#FFFFFF",
  },
  secondary: {
    0: "#000000",
    5: "#1D0D09",
    10: "#291713",
    15: "#34211D",
    20: "#402B27",
    25: "#4C3631",
    30: "#58413C",
    35: "#654D47",
    40: "#725953",
    50: "#8C716B",
    60: "#A78A84",
    70: "#C3A49E",
    80: "#E0BFB8",
    90: "#FDDBD4",
    95: "#FFEDE9",
    98: "#FFF8F6",
    99: "#FFFBFF",
    100: "#FFFFFF",
  },
  tertiary: {
    0: "#000000",
    5: "#171000",
    10: "#231A01",
    15: "#2E2508",
    20: "#3A2F11",
    25: "#453A1B",
    30: "#514626",
    35: "#5E5130",
    40: "#6A5D3B",
    50: "#847652",
    60: "#9F8F69",
    70: "#BAAA82",
    80: "#D6C59B",
    90: "#F3E1B5",
    95: "#FFEFCD",
    98: "#FFF8F1",
    99: "#FFFBFF",
    100: "#FFFFFF",
  },
  neutral: {
    0: "#000000",
    5: "#14100F",
    10: "#1F1A19",
    15: "#2A2523",
    20: "#352F2E",
    25: "#403A39",
    30: "#4C4544",
    35: "#58514F",
    40: "#645D5B",
    50: "#7D7574",
    60: "#978F8D",
    70: "#B2A9A7",
    80: "#CEC4C2",
    90: "#EBE0DE",
    95: "#F9EEEC",
    98: "#FFF8F6",
    99: "#FFFBFF",
    100: "#FFFFFF",
  },
  neutralVariant: {
    0: "#000000",
    5: "#170F0D",
    10: "#231917",
    15: "#2E2321",
    20: "#392E2C",
    25: "#443936",
    30: "#504441",
    35: "#5C504D",
    40: "#695B59",
    50: "#827471",
    60: "#9D8D8A",
    70: "#B8A8A4",
    80: "#D4C3BF",
    90: "#F1DFDB",
    95: "#FFEDE9",
    98: "#FFF8F6",
    99: "#FFFBFF",
    100: "#FFFFFF",
  },
};

// --- WINTER WEATHER schemes (from Material export) ---

const winterLight = {
  primary: "#236488",
  surfaceTint: "#236488",
  onPrimary: "#FFFFFF",
  primaryContainer: "#C7E7FF",
  onPrimaryContainer: "#004C6D",

  secondary: "#4F616E",
  onSecondary: "#FFFFFF",
  secondaryContainer: "#D2E5F5",
  onSecondaryContainer: "#384956",

  tertiary: "#63597C",
  onTertiary: "#FFFFFF",
  tertiaryContainer: "#E9DDFF",
  onTertiaryContainer: "#4B4263",

  error: "#BA1A1A",
  onError: "#FFFFFF",
  errorContainer: "#FFDAD6",
  onErrorContainer: "#93000A",

  background: "#F6FAFE",
  onBackground: "#181C20",
  surface: "#F6FAFE",
  onSurface: "#181C20",

  surfaceVariant: "#DDE3EA",
  onSurfaceVariant: "#41484D",

  outline: "#71787E",
  outlineVariant: "#C1C7CE",

  shadow: "#000000",
  scrim: "#000000",

  inverseSurface: "#2D3135",
  inverseOnSurface: "#EEF1F6",
  inversePrimary: "#93CDF6",

  primaryFixed: "#C7E7FF",
  onPrimaryFixed: "#001E2E",
  primaryFixedDim: "#93CDF6",
  onPrimaryFixedVariant: "#004C6D",

  secondaryFixed: "#D2E5F5",
  onSecondaryFixed: "#0B1D29",
  secondaryFixedDim: "#B6C9D8",
  onSecondaryFixedVariant: "#384956",

  tertiaryFixed: "#E9DDFF",
  onTertiaryFixed: "#1F1635",
  tertiaryFixedDim: "#CDC0E9",
  onTertiaryFixedVariant: "#4B4263",

  surfaceDim: "#D7DADF",
  surfaceBright: "#F6FAFE",
  surfaceContainerLowest: "#FFFFFF",
  surfaceContainerLow: "#F1F4F9",
  surfaceContainer: "#EBEEF3",
  surfaceContainerHigh: "#E5E8ED",
  surfaceContainerHighest: "#DFE3E7",

  primaryHover: hexToRgba("#236488", 0.8),
  tertiaryContainerHover: hexToRgba("#E9DDFF", 0.8),
  outlineButtonHover: hexToRgba("#ffffff", 0.3),
  errorContainerHover: hexToRgba("#FFDAD6", 0.8),

  text: "onSurface",
};

const winterLightMediumContrast = {
  primary: "#003A55",
  surfaceTint: "#236488",
  onPrimary: "#FFFFFF",
  primaryContainer: "#357398",
  onPrimaryContainer: "#FFFFFF",

  secondary: "#273844",
  onSecondary: "#FFFFFF",
  secondaryContainer: "#5E6F7D",
  onSecondaryContainer: "#FFFFFF",

  tertiary: "#3A3151",
  onTertiary: "#FFFFFF",
  tertiaryContainer: "#72688B",
  onTertiaryContainer: "#FFFFFF",

  error: "#740006",
  onError: "#FFFFFF",
  errorContainer: "#CF2C27",
  onErrorContainer: "#FFFFFF",

  background: "#F6FAFE",
  onBackground: "#181C20",
  surface: "#F6FAFE",
  onSurface: "#0D1215",

  surfaceVariant: "#DDE3EA",
  onSurfaceVariant: "#30373C",

  outline: "#4D5359",
  outlineVariant: "#676E74",

  shadow: "#000000",
  scrim: "#000000",

  inverseSurface: "#2D3135",
  inverseOnSurface: "#EEF1F6",
  inversePrimary: "#93CDF6",

  primaryFixed: "#357398",
  onPrimaryFixed: "#FFFFFF",
  primaryFixedDim: "#145A7E",
  onPrimaryFixedVariant: "#FFFFFF",

  secondaryFixed: "#5E6F7D",
  onSecondaryFixed: "#FFFFFF",
  secondaryFixedDim: "#455764",
  onSecondaryFixedVariant: "#FFFFFF",

  tertiaryFixed: "#72688B",
  onTertiaryFixed: "#FFFFFF",
  tertiaryFixedDim: "#595072",
  onTertiaryFixedVariant: "#FFFFFF",

  surfaceDim: "#C3C7CB",
  surfaceBright: "#F6FAFE",
  surfaceContainerLowest: "#FFFFFF",
  surfaceContainerLow: "#F1F4F9",
  surfaceContainer: "#E5E8ED",
  surfaceContainerHigh: "#DADDE2",
  surfaceContainerHighest: "#CED2D7",

  primaryHover: hexToRgba("#357398", 0.8),
  tertiaryContainerHover: hexToRgba("#72688B", 0.8),
  outlineButtonHover: hexToRgba("#ffffff", 0.3),
  errorContainerHover: hexToRgba("#CF2C27", 0.8),

  text: "onSurface",
};

const winterLightHighContrast = {
  primary: "#003046",
  surfaceTint: "#236488",
  onPrimary: "#FFFFFF",
  primaryContainer: "#004E70",
  onPrimaryContainer: "#FFFFFF",

  secondary: "#1D2E3A",
  onSecondary: "#FFFFFF",
  secondaryContainer: "#3A4B58",
  onSecondaryContainer: "#FFFFFF",

  tertiary: "#302747",
  onTertiary: "#FFFFFF",
  tertiaryContainer: "#4D4465",
  onTertiaryContainer: "#FFFFFF",

  error: "#600004",
  onError: "#FFFFFF",
  errorContainer: "#98000A",
  onErrorContainer: "#FFFFFF",

  background: "#F6FAFE",
  onBackground: "#181C20",
  surface: "#F6FAFE",
  onSurface: "#000000",

  surfaceVariant: "#DDE3EA",
  onSurfaceVariant: "#000000",

  outline: "#262D32",
  outlineVariant: "#434A50",

  shadow: "#000000",
  scrim: "#000000",

  inverseSurface: "#2D3135",
  inverseOnSurface: "#FFFFFF",
  inversePrimary: "#93CDF6",

  primaryFixed: "#004E70",
  onPrimaryFixed: "#FFFFFF",
  primaryFixedDim: "#00364F",
  onPrimaryFixedVariant: "#FFFFFF",

  secondaryFixed: "#3A4B58",
  onSecondaryFixed: "#FFFFFF",
  secondaryFixedDim: "#233541",
  onSecondaryFixedVariant: "#FFFFFF",

  tertiaryFixed: "#4D4465",
  onTertiaryFixed: "#FFFFFF",
  tertiaryFixedDim: "#362E4E",
  onTertiaryFixedVariant: "#FFFFFF",

  surfaceDim: "#B5B9BE",
  surfaceBright: "#F6FAFE",
  surfaceContainerLowest: "#FFFFFF",
  surfaceContainerLow: "#EEF1F6",
  surfaceContainer: "#DFE3E7",
  surfaceContainerHigh: "#D1D5D9",
  surfaceContainerHighest: "#C3C7CB",

  primaryHover: hexToRgba("#004E70", 0.8),
  tertiaryContainerHover: hexToRgba("#4D4465", 0.8),
  outlineButtonHover: hexToRgba("#ffffff", 0.3),
  errorContainerHover: hexToRgba("#98000A", 0.8),

  text: "onSurface",
};

const winterDark = {
  primary: "#93CDF6",
  surfaceTint: "#93CDF6",
  onPrimary: "#00344C",
  primaryContainer: "#004C6D",
  onPrimaryContainer: "#C7E7FF",

  secondary: "#B6C9D8",
  onSecondary: "#21323E",
  secondaryContainer: "#384956",
  onSecondaryContainer: "#D2E5F5",

  tertiary: "#CDC0E9",
  onTertiary: "#342B4B",
  tertiaryContainer: "#4B4263",
  onTertiaryContainer: "#E9DDFF",

  error: "#FFB4AB",
  onError: "#690005",
  errorContainer: "#93000A",
  onErrorContainer: "#FFDAD6",

  background: "#101417",
  onBackground: "#DFE3E7",
  surface: "#101417",
  onSurface: "#DFE3E7",

  surfaceVariant: "#41484D",
  onSurfaceVariant: "#C1C7CE",

  outline: "#8B9198",
  outlineVariant: "#41484D",

  shadow: "#000000",
  scrim: "#000000",

  inverseSurface: "#DFE3E7",
  inverseOnSurface: "#2D3135",
  inversePrimary: "#236488",

  primaryFixed: "#C7E7FF",
  onPrimaryFixed: "#001E2E",
  primaryFixedDim: "#93CDF6",
  onPrimaryFixedVariant: "#004C6D",

  secondaryFixed: "#D2E5F5",
  onSecondaryFixed: "#0B1D29",
  secondaryFixedDim: "#B6C9D8",
  onSecondaryFixedVariant: "#384956",

  tertiaryFixed: "#E9DDFF",
  onTertiaryFixed: "#1F1635",
  tertiaryFixedDim: "#CDC0E9",
  onTertiaryFixedVariant: "#4B4263",

  surfaceDim: "#101417",
  surfaceBright: "#353A3D",
  surfaceContainerLowest: "#0A0F12",
  surfaceContainerLow: "#181C20",
  surfaceContainer: "#1C2024",
  surfaceContainerHigh: "#262A2E",
  surfaceContainerHighest: "#313539",

  primaryHover: hexToRgba("#93CDF6", 0.8),
  tertiaryContainerHover: hexToRgba("#4B4263", 0.8),
  outlineButtonHover: hexToRgba("#000000", 0.2),
  errorContainerHover: hexToRgba("#93000A", 0.8),

  text: "onSurface",
};

const winterDarkMediumContrast = {
  primary: "#BAE1FF",
  surfaceTint: "#93CDF6",
  onPrimary: "#00293D",
  primaryContainer: "#5C97BD",
  onPrimaryContainer: "#000000",

  secondary: "#CCDFEE",
  onSecondary: "#162833",
  secondaryContainer: "#8193A1",
  onSecondaryContainer: "#000000",

  tertiary: "#E3D6FF",
  onTertiary: "#292040",
  tertiaryContainer: "#968BB1",
  onTertiaryContainer: "#000000",

  error: "#FFD2CC",
  onError: "#540003",
  errorContainer: "#FF5449",
  onErrorContainer: "#000000",

  background: "#101417",
  onBackground: "#DFE3E7",
  surface: "#101417",
  onSurface: "#FFFFFF",

  surfaceVariant: "#41484D",
  onSurfaceVariant: "#D7DDE4",

  outline: "#ACB3B9",
  outlineVariant: "#8B9197",

  shadow: "#000000",
  scrim: "#000000",

  inverseSurface: "#DFE3E7",
  inverseOnSurface: "#262B2E",
  inversePrimary: "#004D6E",

  primaryFixed: "#C7E7FF",
  onPrimaryFixed: "#00131F",
  primaryFixedDim: "#93CDF6",
  onPrimaryFixedVariant: "#003A55",

  secondaryFixed: "#D2E5F5",
  onSecondaryFixed: "#02131E",
  secondaryFixedDim: "#B6C9D8",
  onSecondaryFixedVariant: "#273844",

  tertiaryFixed: "#E9DDFF",
  onTertiaryFixed: "#140B2A",
  tertiaryFixedDim: "#CDC0E9",
  onTertiaryFixedVariant: "#3A3151",

  surfaceDim: "#101417",
  surfaceBright: "#414549",
  surfaceContainerLowest: "#05080B",
  surfaceContainerLow: "#1A1E22",
  surfaceContainer: "#24282C",
  surfaceContainerHigh: "#2F3337",
  surfaceContainerHighest: "#3A3E42",

  primaryHover: hexToRgba("#BAE1FF", 0.8),
  tertiaryContainerHover: hexToRgba("#968BB1", 0.8),
  outlineButtonHover: hexToRgba("#000000", 0.2),
  errorContainerHover: hexToRgba("#FF5449", 0.8),

  text: "onSurface",
};

const winterDarkHighContrast = {
  primary: "#E3F2FF",
  surfaceTint: "#93CDF6",
  onPrimary: "#000000",
  primaryContainer: "#8FC9F2",
  onPrimaryContainer: "#000D16",

  secondary: "#E3F2FF",
  onSecondary: "#000000",
  secondaryContainer: "#B3C5D4",
  onSecondaryContainer: "#000D16",

  tertiary: "#F5EDFF",
  onTertiary: "#000000",
  tertiaryContainer: "#C9BCE5",
  onTertiaryContainer: "#0E0624",

  error: "#FFECE9",
  onError: "#000000",
  errorContainer: "#FFAEA4",
  onErrorContainer: "#220001",

  background: "#101417",
  onBackground: "#DFE3E7",
  surface: "#101417",
  onSurface: "#FFFFFF",

  surfaceVariant: "#41484D",
  onSurfaceVariant: "#FFFFFF",

  outline: "#EBF1F8",
  outlineVariant: "#BDC3CA",

  shadow: "#000000",
  scrim: "#000000",

  inverseSurface: "#DFE3E7",
  inverseOnSurface: "#000000",
  inversePrimary: "#004D6E",

  primaryFixed: "#C7E7FF",
  onPrimaryFixed: "#000000",
  primaryFixedDim: "#93CDF6",
  onPrimaryFixedVariant: "#00131F",

  secondaryFixed: "#D2E5F5",
  onSecondaryFixed: "#000000",
  secondaryFixedDim: "#B6C9D8",
  onSecondaryFixedVariant: "#02131E",

  tertiaryFixed: "#E9DDFF",
  onTertiaryFixed: "#000000",
  tertiaryFixedDim: "#CDC0E9",
  onTertiaryFixedVariant: "#140B2A",

  surfaceDim: "#101417",
  surfaceBright: "#4C5155",
  surfaceContainerLowest: "#000000",
  surfaceContainerLow: "#1C2024",
  surfaceContainer: "#2D3135",
  surfaceContainerHigh: "#383C40",
  surfaceContainerHighest: "#43474B",

  primaryHover: hexToRgba("#E3F2FF", 0.8),
  tertiaryContainerHover: hexToRgba("#C9BCE5", 0.8),
  outlineButtonHover: hexToRgba("#000000", 0.2),
  errorContainerHover: hexToRgba("#FFAEA4", 0.8),

  text: "onSurface",
};

// --- WINTER palettes (for token‑level use) ---

const winterPalettes = {
  primary: {
    0: "#000000",
    5: "#00131F",
    10: "#001E2E",
    15: "#00293D",
    20: "#00344C",
    25: "#00405C",
    30: "#004C6D",
    35: "#0E587B",
    40: "#226488",
    50: "#407DA2",
    60: "#5C97BE",
    70: "#77B2D9",
    80: "#93CDF6",
    90: "#C7E7FF",
    95: "#E5F2FF",
    98: "#F6FAFF",
    99: "#FBFCFF",
    100: "#FFFFFF",
  },
  secondary: {
    0: "#000000",
    5: "#07121A",
    10: "#111D25",
    15: "#1C272F",
    20: "#26323A",
    25: "#313D46",
    30: "#3D4851",
    35: "#48545D",
    40: "#546069",
    50: "#6D7982",
    60: "#86929C",
    70: "#A1ADB7",
    80: "#BCC8D3",
    90: "#D8E4EF",
    95: "#E6F2FE",
    98: "#F6FAFF",
    99: "#FBFCFF",
    100: "#FFFFFF",
  },
  tertiary: {
    0: "#000000",
    5: "#130D22",
    10: "#1E182D",
    15: "#282238",
    20: "#332D43",
    25: "#3E384F",
    30: "#4A435B",
    35: "#564F67",
    40: "#625B73",
    50: "#7B738D",
    60: "#958DA7",
    70: "#B0A7C3",
    80: "#CCC2DF",
    90: "#E8DEFB",
    95: "#F6EEFF",
    98: "#FDF7FF",
    99: "#FFFBFF",
    100: "#FFFFFF",
  },
  neutral: {
    0: "#000000",
    5: "#0F1113",
    10: "#1A1C1D",
    15: "#242627",
    20: "#2F3032",
    25: "#3A3B3D",
    30: "#464748",
    35: "#515254",
    40: "#5D5E60",
    50: "#767779",
    60: "#909192",
    70: "#ABABAD",
    80: "#C6C6C8",
    90: "#E3E2E4",
    95: "#F1F0F2",
    98: "#FAF9FB",
    99: "#FDFCFE",
    100: "#FFFFFF",
  },
  neutralVariant: {
    0: "#000000",
    5: "#0D1115",
    10: "#181C20",
    15: "#22262A",
    20: "#2D3135",
    25: "#383C40",
    30: "#43474B",
    35: "#4F5357",
    40: "#5B5F63",
    50: "#73777C",
    60: "#8D9195",
    70: "#A8ACB0",
    80: "#C3C7CB",
    90: "#DFE3E7",
    95: "#EEF1F6",
    98: "#F6FAFE",
    99: "#FBFCFF",
    100: "#FFFFFF",
  },
};

// --- FLOODING schemes (from Material export) ---

const floodingLight = {
  primary: "#00696F",
  surfaceTint: "#00696F",
  onPrimary: "#FFFFFF",
  primaryContainer: "#9DF0F7",
  onPrimaryContainer: "#004F54",

  secondary: "#4A6365",
  onSecondary: "#FFFFFF",
  secondaryContainer: "#CCE8EA",
  onSecondaryContainer: "#324B4D",

  tertiary: "#4F5F7D",
  onTertiary: "#FFFFFF",
  tertiaryContainer: "#D7E3FF",
  onTertiaryContainer: "#374764",

  error: "#BA1A1A",
  onError: "#FFFFFF",
  errorContainer: "#FFDAD6",
  onErrorContainer: "#93000A",

  background: "#F4FAFB",
  onBackground: "#161D1D",
  surface: "#F4FAFB",
  onSurface: "#161D1D",

  surfaceVariant: "#DAE4E5",
  onSurfaceVariant: "#3F4849",

  outline: "#6F797A",
  outlineVariant: "#BEC8C9",

  shadow: "#000000",
  scrim: "#000000",

  inverseSurface: "#2B3232",
  inverseOnSurface: "#ECF2F2",
  inversePrimary: "#80D4DB",

  primaryFixed: "#9DF0F7",
  onPrimaryFixed: "#002022",
  primaryFixedDim: "#80D4DB",
  onPrimaryFixedVariant: "#004F54",

  secondaryFixed: "#CCE8EA",
  onSecondaryFixed: "#051F21",
  secondaryFixedDim: "#B1CBCE",
  onSecondaryFixedVariant: "#324B4D",

  tertiaryFixed: "#D7E3FF",
  onTertiaryFixed: "#091B36",
  tertiaryFixedDim: "#B7C7EA",
  onTertiaryFixedVariant: "#374764",

  surfaceDim: "#D5DBDB",
  surfaceBright: "#F4FAFB",
  surfaceContainerLowest: "#FFFFFF",
  surfaceContainerLow: "#EFF5F5",
  surfaceContainer: "#E9EFEF",
  surfaceContainerHigh: "#E3E9E9",
  surfaceContainerHighest: "#DDE4E4",

  primaryHover: hexToRgba("#00696F", 0.8),
  tertiaryContainerHover: hexToRgba("#D7E3FF", 0.8),
  outlineButtonHover: hexToRgba("#ffffff", 0.3),
  errorContainerHover: hexToRgba("#FFDAD6", 0.8),

  text: "onSurface",
};

const floodingLightMediumContrast = {
  primary: "#003D41",
  surfaceTint: "#00696F",
  onPrimary: "#FFFFFF",
  primaryContainer: "#16797F",
  onPrimaryContainer: "#FFFFFF",

  secondary: "#213A3C",
  onSecondary: "#FFFFFF",
  secondaryContainer: "#587274",
  onSecondaryContainer: "#FFFFFF",

  tertiary: "#263652",
  onTertiary: "#FFFFFF",
  tertiaryContainer: "#5D6D8C",
  onTertiaryContainer: "#FFFFFF",

  error: "#740006",
  onError: "#FFFFFF",
  errorContainer: "#CF2C27",
  onErrorContainer: "#FFFFFF",

  background: "#F4FAFB",
  onBackground: "#161D1D",
  surface: "#F4FAFB",
  onSurface: "#0C1213",

  surfaceVariant: "#DAE4E5",
  onSurfaceVariant: "#2E3839",

  outline: "#4B5455",
  outlineVariant: "#656F70",

  shadow: "#000000",
  scrim: "#000000",

  inverseSurface: "#2B3232",
  inverseOnSurface: "#ECF2F2",
  inversePrimary: "#80D4DB",

  primaryFixed: "#16797F",
  onPrimaryFixed: "#FFFFFF",
  primaryFixedDim: "#005E64",
  onPrimaryFixedVariant: "#FFFFFF",

  secondaryFixed: "#587274",
  onSecondaryFixed: "#FFFFFF",
  secondaryFixedDim: "#40595B",
  onSecondaryFixedVariant: "#FFFFFF",

  tertiaryFixed: "#5D6D8C",
  onTertiaryFixed: "#FFFFFF",
  tertiaryFixedDim: "#455573",
  onTertiaryFixedVariant: "#FFFFFF",

  surfaceDim: "#C1C8C8",
  surfaceBright: "#F4FAFB",
  surfaceContainerLowest: "#FFFFFF",
  surfaceContainerLow: "#EFF5F5",
  surfaceContainer: "#E3E9E9",
  surfaceContainerHigh: "#D8DEDE",
  surfaceContainerHighest: "#CDD3D3",

  primaryHover: hexToRgba("#16797F", 0.8),
  tertiaryContainerHover: hexToRgba("#5D6D8C", 0.8),
  outlineButtonHover: hexToRgba("#ffffff", 0.3),
  errorContainerHover: hexToRgba("#CF2C27", 0.8),

  text: "onSurface",
};

const floodingLightHighContrast = {
  primary: "#003235",
  surfaceTint: "#00696F",
  onPrimary: "#FFFFFF",
  primaryContainer: "#005257",
  onPrimaryContainer: "#FFFFFF",

  secondary: "#173032",
  onSecondary: "#FFFFFF",
  secondaryContainer: "#354D4F",
  onSecondaryContainer: "#FFFFFF",

  tertiary: "#1C2C48",
  onTertiary: "#FFFFFF",
  tertiaryContainer: "#3A4966",
  onTertiaryContainer: "#FFFFFF",

  error: "#600004",
  onError: "#FFFFFF",
  errorContainer: "#98000A",
  onErrorContainer: "#FFFFFF",

  background: "#F4FAFB",
  onBackground: "#161D1D",
  surface: "#F4FAFB",
  onSurface: "#000000",

  surfaceVariant: "#DAE4E5",
  onSurfaceVariant: "#000000",

  outline: "#242E2F",
  outlineVariant: "#414B4C",

  shadow: "#000000",
  scrim: "#000000",

  inverseSurface: "#2B3232",
  inverseOnSurface: "#FFFFFF",
  inversePrimary: "#80D4DB",

  primaryFixed: "#005257",
  onPrimaryFixed: "#FFFFFF",
  primaryFixedDim: "#00393D",
  onPrimaryFixedVariant: "#FFFFFF",

  secondaryFixed: "#354D4F",
  onSecondaryFixed: "#FFFFFF",
  secondaryFixedDim: "#1E3739",
  onSecondaryFixedVariant: "#FFFFFF",

  tertiaryFixed: "#3A4966",
  onTertiaryFixed: "#FFFFFF",
  tertiaryFixedDim: "#23334F",
  onTertiaryFixedVariant: "#FFFFFF",

  surfaceDim: "#B4BABA",
  surfaceBright: "#F4FAFB",
  surfaceContainerLowest: "#FFFFFF",
  surfaceContainerLow: "#ECF2F2",
  surfaceContainer: "#DDE4E4",
  surfaceContainerHigh: "#CFD6D6",
  surfaceContainerHighest: "#C1C8C8",

  primaryHover: hexToRgba("#005257", 0.8),
  tertiaryContainerHover: hexToRgba("#3A4966", 0.8),
  outlineButtonHover: hexToRgba("#ffffff", 0.3),
  errorContainerHover: hexToRgba("#98000A", 0.8),

  text: "onSurface",
};

const floodingDark = {
  primary: "#80D4DB",
  surfaceTint: "#80D4DB",
  onPrimary: "#00363A",
  primaryContainer: "#004F54",
  onPrimaryContainer: "#9DF0F7",

  secondary: "#B1CBCE",
  onSecondary: "#1B3436",
  secondaryContainer: "#324B4D",
  onSecondaryContainer: "#CCE8EA",

  tertiary: "#B7C7EA",
  onTertiary: "#20304C",
  tertiaryContainer: "#374764",
  onTertiaryContainer: "#D7E3FF",

  error: "#FFB4AB",
  onError: "#690005",
  errorContainer: "#93000A",
  onErrorContainer: "#FFDAD6",

  background: "#0E1415",
  onBackground: "#DDE4E4",
  surface: "#0E1415",
  onSurface: "#DDE4E4",

  surfaceVariant: "#3F4849",
  onSurfaceVariant: "#BEC8C9",

  outline: "#899393",
  outlineVariant: "#3F4849",

  shadow: "#000000",
  scrim: "#000000",

  inverseSurface: "#DDE4E4",
  inverseOnSurface: "#2B3232",
  inversePrimary: "#00696F",

  primaryFixed: "#9DF0F7",
  onPrimaryFixed: "#002022",
  primaryFixedDim: "#80D4DB",
  onPrimaryFixedVariant: "#004F54",

  secondaryFixed: "#CCE8EA",
  onSecondaryFixed: "#051F21",
  secondaryFixedDim: "#B1CBCE",
  onSecondaryFixedVariant: "#324B4D",

  tertiaryFixed: "#D7E3FF",
  onTertiaryFixed: "#091B36",
  tertiaryFixedDim: "#B7C7EA",
  onTertiaryFixedVariant: "#374764",

  surfaceDim: "#0E1415",
  surfaceBright: "#343A3B",
  surfaceContainerLowest: "#090F10",
  surfaceContainerLow: "#161D1D",
  surfaceContainer: "#1A2121",
  surfaceContainerHigh: "#252B2C",
  surfaceContainerHighest: "#303636",

  primaryHover: hexToRgba("#80D4DB", 0.8),
  tertiaryContainerHover: hexToRgba("#374764", 0.8),
  outlineButtonHover: hexToRgba("#000000", 0.2),
  errorContainerHover: hexToRgba("#93000A", 0.8),

  text: "onSurface",
};

const floodingDarkMediumContrast = {
  primary: "#96EAF1",
  surfaceTint: "#80D4DB",
  onPrimary: "#002B2E",
  primaryContainer: "#479DA4",
  onPrimaryContainer: "#000000",

  secondary: "#C6E1E4",
  onSecondary: "#10292B",
  secondaryContainer: "#7B9598",
  onSecondaryContainer: "#000000",

  tertiary: "#CDDDFF",
  onTertiary: "#152641",
  tertiaryContainer: "#8191B2",
  onTertiaryContainer: "#000000",

  error: "#FFD2CC",
  onError: "#540003",
  errorContainer: "#FF5449",
  onErrorContainer: "#000000",

  background: "#0E1415",
  onBackground: "#DDE4E4",
  surface: "#0E1415",
  onSurface: "#FFFFFF",

  surfaceVariant: "#3F4849",
  onSurfaceVariant: "#D4DEDF",

  outline: "#AAB4B4",
  outlineVariant: "#889293",

  shadow: "#000000",
  scrim: "#000000",

  inverseSurface: "#DDE4E4",
  inverseOnSurface: "#252B2C",
  inversePrimary: "#005055",

  primaryFixed: "#9DF0F7",
  onPrimaryFixed: "#001416",
  primaryFixedDim: "#80D4DB",
  onPrimaryFixedVariant: "#003D41",

  secondaryFixed: "#CCE8EA",
  onSecondaryFixed: "#001416",
  secondaryFixedDim: "#B1CBCE",
  onSecondaryFixedVariant: "#213A3C",

  tertiaryFixed: "#D7E3FF",
  onTertiaryFixed: "#00112B",
  tertiaryFixedDim: "#B7C7EA",
  onTertiaryFixedVariant: "#263652",

  surfaceDim: "#0E1415",
  surfaceBright: "#3F4646",
  surfaceContainerLowest: "#040809",
  surfaceContainerLow: "#181F1F",
  surfaceContainer: "#232929",
  surfaceContainerHigh: "#2D3434",
  surfaceContainerHighest: "#383F3F",

  primaryHover: hexToRgba("#96EAF1", 0.8),
  tertiaryContainerHover: hexToRgba("#8191B2", 0.8),
  outlineButtonHover: hexToRgba("#000000", 0.2),
  errorContainerHover: hexToRgba("#FF5449", 0.8),

  text: "onSurface",
};

const floodingDarkHighContrast = {
  primary: "#C1FAFF",
  surfaceTint: "#80D4DB",
  onPrimary: "#000000",
  primaryContainer: "#7CD0D7",
  onPrimaryContainer: "#000E0F",

  secondary: "#DAF5F7",
  onSecondary: "#000000",
  secondaryContainer: "#ADC8CA",
  onSecondaryContainer: "#000E0F",

  tertiary: "#EBF0FF",
  onTertiary: "#000000",
  tertiaryContainer: "#B3C3E6",
  onTertiaryContainer: "#000B20",

  error: "#FFECE9",
  onError: "#000000",
  errorContainer: "#FFAEA4",
  onErrorContainer: "#220001",

  background: "#0E1415",
  onBackground: "#DDE4E4",
  surface: "#0E1415",
  onSurface: "#FFFFFF",

  surfaceVariant: "#3F4849",
  onSurfaceVariant: "#FFFFFF",

  outline: "#E8F2F2",
  outlineVariant: "#BAC4C5",

  shadow: "#000000",
  scrim: "#000000",

  inverseSurface: "#DDE4E4",
  inverseOnSurface: "#000000",
  inversePrimary: "#005055",

  primaryFixed: "#9DF0F7",
  onPrimaryFixed: "#000000",
  primaryFixedDim: "#80D4DB",
  onPrimaryFixedVariant: "#001416",

  secondaryFixed: "#CCE8EA",
  onSecondaryFixed: "#000000",
  secondaryFixedDim: "#B1CBCE",
  onSecondaryFixedVariant: "#001416",

  tertiaryFixed: "#D7E3FF",
  onTertiaryFixed: "#000000",
  tertiaryFixedDim: "#B7C7EA",
  onTertiaryFixedVariant: "#00112B",

  surfaceDim: "#0E1415",
  surfaceBright: "#4B5152",
  surfaceContainerLowest: "#000000",
  surfaceContainerLow: "#1A2121",
  surfaceContainer: "#2B3232",
  surfaceContainerHigh: "#363D3D",
  surfaceContainerHighest: "#414848",

  primaryHover: hexToRgba("#C1FAFF", 0.8),
  tertiaryContainerHover: hexToRgba("#B3C3E6", 0.8),
  outlineButtonHover: hexToRgba("#000000", 0.2),
  errorContainerHover: hexToRgba("#FFAEA4", 0.8),

  text: "onSurface",
};

// --- FLOODING palettes ---

const floodingPalettes = {
  primary: {
    0: "#000000",
    5: "#001416",
    10: "#002022",
    15: "#002B2E",
    20: "#00363A",
    25: "#004347",
    30: "#004F54",
    35: "#005C61",
    40: "#00696F",
    50: "#17848B",
    60: "#3E9EA5",
    70: "#5CB9C0",
    80: "#79D5DC",
    90: "#96F1F9",
    95: "#C4FAFF",
    98: "#E8FDFF",
    99: "#F3FEFF",
    100: "#FFFFFF",
  },
  secondary: {
    0: "#000000",
    5: "#031415",
    10: "#0C1E20",
    15: "#17292A",
    20: "#223335",
    25: "#2D3E40",
    30: "#384A4B",
    35: "#445657",
    40: "#4F6263",
    50: "#687A7C",
    60: "#819496",
    70: "#9CAFB0",
    80: "#B7CACC",
    90: "#D2E6E8",
    95: "#E1F5F6",
    98: "#E9FDFF",
    99: "#F3FEFF",
    100: "#FFFFFF",
  },
  tertiary: {
    0: "#000000",
    5: "#051124",
    10: "#101C2F",
    15: "#1A263A",
    20: "#253145",
    25: "#303C51",
    30: "#3C475D",
    35: "#475369",
    40: "#535F75",
    50: "#6C778F",
    60: "#8591AA",
    70: "#A0ABC5",
    80: "#BBC7E1",
    90: "#D7E3FE",
    95: "#ECF0FF",
    98: "#F9F9FF",
    99: "#FDFBFF",
    100: "#FFFFFF",
  },
  neutral: {
    0: "#000000",
    5: "#0F1112",
    10: "#191C1C",
    15: "#242626",
    20: "#2E3131",
    25: "#393C3C",
    30: "#454747",
    35: "#505353",
    40: "#5C5F5F",
    50: "#757777",
    60: "#8F9191",
    70: "#AAABAB",
    80: "#C5C7C6",
    90: "#E2E3E2",
    95: "#F0F1F0",
    98: "#F9F9F9",
    99: "#FCFCFC",
    100: "#FFFFFF",
  },
  neutralVariant: {
    0: "#000000",
    5: "#0B1213",
    10: "#161D1D",
    15: "#202728",
    20: "#2B3232",
    25: "#363D3D",
    30: "#414849",
    35: "#4D5454",
    40: "#596060",
    50: "#717879",
    60: "#8B9292",
    70: "#A5ACAD",
    80: "#C1C8C8",
    90: "#DDE4E4",
    95: "#EBF2F2",
    98: "#F4FBFB",
    99: "#F7FDFE",
    100: "#FFFFFF",
  },
};


// --- EXTREME HEAT schemes (MD3 Standard) ---

const extremeHeatLight = {
  primary: "#A23F00", // Searing Molten Orange
  surfaceTint: "#A23F00",
  onPrimary: "#FFFFFF",
  primaryContainer: "#FFDBCF",
  onPrimaryContainer: "#370F00",

  secondary: "#775447", // Sun-baked clay
  onSecondary: "#FFFFFF",
  secondaryContainer: "#FFDBCF",
  onSecondaryContainer: "#2B160F",

  tertiary: "#824F75", // Solar UV / Heat Shimmer Magenta
  onTertiary: "#FFFFFF",
  tertiaryContainer: "#FFD7F3",
  onTertiaryContainer: "#340D2E",

  error: "#BA1A1A",
  onError: "#FFFFFF",
  errorContainer: "#FFDAD6",
  onErrorContainer: "#93000A",

  background: "#FFF8F5", // Bleached, hot sandy white
  onBackground: "#231A17",
  surface: "#FFF8F5",
  onSurface: "#231A17",

  surfaceVariant: "#F5DED6",
  onSurfaceVariant: "#53433E",

  outline: "#85736C",
  outlineVariant: "#D8C2BA",

  shadow: "#000000",
  scrim: "#000000",

  inverseSurface: "#392E2B",
  inverseOnSurface: "#FFEDE8",
  inversePrimary: "#FFB596",

  primaryFixed: "#FFDBCF",
  onPrimaryFixed: "#370F00",
  primaryFixedDim: "#FFB596",
  onPrimaryFixedVariant: "#7C2E00",

  secondaryFixed: "#FFDBCF",
  onSecondaryFixed: "#2B160F",
  secondaryFixedDim: "#E6BEB1",
  onSecondaryFixedVariant: "#5C3D31",

  tertiaryFixed: "#FFD7F3",
  onTertiaryFixed: "#340D2E",
  tertiaryFixedDim: "#F4B4E1",
  onTertiaryFixedVariant: "#68385C",

  surfaceDim: "#E8D7D2",
  surfaceBright: "#FFF8F5",
  surfaceContainerLowest: "#FFFFFF",
  surfaceContainerLow: "#FFF1EC",
  surfaceContainer: "#FCEAE4",
  surfaceContainerHigh: "#F7E4DE",
  surfaceContainerHighest: "#F1DED8",

  primaryHover: hexToRgba("#A23F00", 0.8),
  tertiaryContainerHover: hexToRgba("#FFD7F3", 0.8),
  outlineButtonHover: hexToRgba("#ffffff", 0.3),
  errorContainerHover: hexToRgba("#FFDAD6", 0.8),

  text: "onSurface",
};

const extremeHeatDark = {
  primary: "#FFB596", // Blazing Neon Amber
  surfaceTint: "#FFB596",
  onPrimary: "#571E00",
  primaryContainer: "#7C2E00",
  onPrimaryContainer: "#FFDBCF",

  secondary: "#E6BEB1",
  onSecondary: "#44271C",
  secondaryContainer: "#5C3D31",
  onSecondaryContainer: "#FFDBCF",

  tertiary: "#F4B4E1", // Incandescent Violet
  onTertiary: "#4E2245",
  tertiaryContainer: "#68385C",
  onTertiaryContainer: "#FFD7F3",

  error: "#FFB4AB",
  onError: "#690005",
  errorContainer: "#93000A",
  onErrorContainer: "#FFDAD6",

  background: "#1A120E", // Oppressive dark heat dome
  onBackground: "#F1DED8",
  surface: "#1A120E",
  onSurface: "#F1DED8",

  surfaceVariant: "#53433E",
  onSurfaceVariant: "#D8C2BA",

  outline: "#A08D85",
  outlineVariant: "#53433E",

  shadow: "#000000",
  scrim: "#000000",

  inverseSurface: "#F1DED8",
  inverseOnSurface: "#392E2B",
  inversePrimary: "#A23F00",

  primaryFixed: "#FFDBCF",
  onPrimaryFixed: "#370F00",
  primaryFixedDim: "#FFB596",
  onPrimaryFixedVariant: "#7C2E00",

  secondaryFixed: "#FFDBCF",
  onSecondaryFixed: "#2B160F",
  secondaryFixedDim: "#E6BEB1",
  onSecondaryFixedVariant: "#5C3D31",

  tertiaryFixed: "#FFD7F3",
  onTertiaryFixed: "#340D2E",
  tertiaryFixedDim: "#F4B4E1",
  onTertiaryFixedVariant: "#68385C",

  surfaceDim: "#1A120E",
  surfaceBright: "#423733",
  surfaceContainerLowest: "#140D0A",
  surfaceContainerLow: "#231A17",
  surfaceContainer: "#271E1B",
  surfaceContainerHigh: "#322925",
  surfaceContainerHighest: "#3D332F",

  primaryHover: hexToRgba("#FFB596", 0.8),
  tertiaryContainerHover: hexToRgba("#68385C", 0.8),
  outlineButtonHover: hexToRgba("#000000", 0.2),
  errorContainerHover: hexToRgba("#93000A", 0.8),

  text: "onSurface",
};

// --- EXTREME HEAT PALETTES (Tonal Ranges) ---

const extremeHeatPalettes = {
  primary: {
    0: "#000000",
    10: "#370F00",
    20: "#571E00",
    30: "#7C2E00",
    40: "#A23F00",
    50: "#C25316",
    60: "#E36A2F",
    70: "#FF8A53",
    80: "#FFB596",
    90: "#FFDBCF",
    95: "#FFEDE6",
    99: "#FFFBFA",
    100: "#FFFFFF",
  },
  secondary: {
    0: "#000000",
    10: "#2B160F",
    20: "#44271C",
    30: "#5C3D31",
    40: "#775447",
    50: "#926C5F",
    60: "#AE8577",
    70: "#CAA091",
    80: "#E6BEB1",
    90: "#FFDBCF",
    95: "#FFEDE6",
    99: "#FFFBFA",
    100: "#FFFFFF",
  },
  tertiary: {
    0: "#000000",
    10: "#340D2E",
    20: "#4E2245",
    30: "#68385C",
    40: "#824F75",
    50: "#9D678E",
    60: "#B980A9",
    70: "#D699C3",
    80: "#F4B4E1",
    90: "#FFD7F3",
    95: "#FFEBFA",
    99: "#FFFBFF",
    100: "#FFFFFF",
  },
  neutral: {
    0: "#000000",
    10: "#231A17",
    20: "#392E2B",
    30: "#504440",
    40: "#695C57",
    50: "#827570",
    60: "#9D8E8A",
    70: "#B8A9A4",
    80: "#D4C4BF",
    90: "#F1DED8",
    95: "#FFF1EC",
    99: "#FFFBFA",
    100: "#FFFFFF",
  },
  neutralVariant: {
    0: "#000000",
    10: "#261915",
    20: "#3C2E2A",
    30: "#53433E",
    40: "#6C5A55",
    50: "#86736D",
    60: "#A18C86",
    70: "#BCA7A0",
    80: "#D8C2BA",
    90: "#F5DED6",
    95: "#FFEDE6",
    99: "#FFFBFA",
    100: "#FFFFFF",
  },
};
// --- Derived tokens from palettes (for easier use in components) ---
const withPaletteSteps = (scheme, palettes) => {
  const result = { ...scheme };

  const flattenChannel = (channelName) => {
    const channel = palettes[channelName];
    if (!channel) return;

    Object.entries(channel).forEach(([step, color]) => {
      // e.g. "primary10", "secondary80", "neutralVariant95"
      const key = `${channelName}${step}`;
      // don't overwrite any explicitly defined scheme keys
      if (result[key] == null) {
        result[key] = color;
      }
    });
  };

  flattenChannel("primary");
  flattenChannel("secondary");
  flattenChannel("tertiary");
  flattenChannel("neutral");
  flattenChannel("neutralVariant");
  flattenChannel("error");

  return result;
};

// ---- QUICK TOGGLE: severe-only vs multi-hazard ----
// ---- QUICK TOGGLE: severe-only vs multi-hazard ----
const ENABLE_HAZARD_MODES = false;

// Base colors: default scheme is severe dark, with derived palette steps
const baseColors = {
  // severe dark is the default base scheme
  ...withPaletteSteps(severeDark, severePalettes),

  // keep the raw palettes attached for advanced use if needed
  palettes: {
    severe: severePalettes,
    winter: winterPalettes,
    wildfire: wildfirePalettes,
    flooding: floodingPalettes,
  },
};

// Hazard-specific color modes
const hazardModes = ENABLE_HAZARD_MODES
  ? {
      // "all" (no specific hazard) uses severe dark
      all: withPaletteSteps(severeDark, severePalettes),

      // dark hazard modes
      severe: withPaletteSteps(severeDark, severePalettes),
      winter: withPaletteSteps(winterDark, winterPalettes),
      wildfire: withPaletteSteps(wildfireDark, wildfirePalettes),
      flooding: withPaletteSteps(floodingDark, floodingPalettes),

      // light variants if you ever use these as colorMode names
      severelight: withPaletteSteps(severeLight, severePalettes),
      winterlight: withPaletteSteps(winterLight, winterPalettes),
      wildfirelight: withPaletteSteps(wildfireLight, wildfirePalettes),
      floodinglight: withPaletteSteps(floodingLight, floodingPalettes),
    }
  : {};

export default merge(roboto, {
  breakpoints: ["40em", "63em", "64em"],
  space: [0, 4, 8, 16, 32, 64, 128, 256, 512],
  fonts: {
    body: '"Nunito Sans", sans-serif',
    heading: '"Nunito Sans", sans-serif',
    monospace: "Menlo, monospace",
  },
  fontSizes: [11, 12, 14, 16, 22, 24, 28, 32, 36, 45, 57],
  fontWeights: {
    extraLight: 200,
    light: 300,
    regular: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
    extraBold: 800,
    black: 900,
    extraBlack: 1000,
  },
  lineHeights: {
    display: 1.12,
    displayMedium: 1.16,
    displaySmall: 1.22,
    headline: 1.25,
    headlineMedium: 1.29,
    headlineSmall: 1.33,
    titleLarge: 1.27,
    titleMedium: 1.5,
    titleSmall: 1.43,
    bodyLarge: 1.5,
    bodyMedium: 1.43,
    bodySmall: 1.33,
    labelLarge: 1.43,
    labelMedium: 1.33,
    labelSmall: 1.45,
  },
  letterSpacings: {
    displayLarge: "-0.25px",
    displayMedium: "0px",
    displaySmall: "0px",
    headlineLarge: "0px",
    headlineMedium: "0px",
    headlineSmall: "0px",
    titleLarge: "0px",
    titleMedium: "0.15px",
    titleSmall: "0.1px",
    bodyLarge: "0.5px",
    bodyMedium: "0.25px",
    bodySmall: "0.4px",
    labelLarge: "0.1px",
    labelMedium: "0.5px",
    labelSmall: "0.5px",
  },

colors: {
  ...baseColors,
  modes: hazardModes,

  scrollbarThumb: hexToRgba("#D0C5B4", 0.42),
  scrollbarThumbHover: hexToRgba("#D0C5B4", 0.68),
  scrollbarTrack: "transparent",
  scrollbarCorner: "transparent",
},

  initialColorModeName: "severe",
  printColorModeName: "severe",
  initialColorModeName: "severe",
  printColorModeName: "severe",

  shadows: {
    boxShadow1dp:
      "rgba(0, 0, 0, 0.2) 0px 2px 1px -1px, rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, rgba(0, 0, 0, 0.12) 0px 1px 3px 0px",
    boxShadow3dp:
      "rgba(0, 0, 0, 0.2) 0px 3px 3px -2px, rgba(0, 0, 0, 0.14) 0px 3px 4px 0px, rgba(0, 0, 0, 0.12) 0px 1px 8px 0px;",
    boxShadow6dp:
      "rgba(0, 0, 0, 0.2) 0px 3px 5px -1px, rgba(0, 0, 0, 0.14) 0px 6px 10px 0px, rgba(0, 0, 0, 0.12) 0px 1px 18px 0px;",
    boxShadow8dp:
      "rgba(0, 0, 0, 0.2) 0px 5px 5px -3px, rgba(0, 0, 0, 0.14) 0px 8px 10px 1px, rgba(0, 0, 0, 0.12) 0px 3px 14px 2px;",
    boxShadow12dp:
      "rgba(0, 0, 0, 0.2) 0px 7px 8px -4px, rgba(0, 0, 0, 0.14) 0px 12px 17px 2px, rgba(0, 0, 0, 0.12) 0px 5px 22px 4px;",
  },

  forms: {
    select: {
      fontFamily: "body",
      fontSize: 1,
      lineHeight: "body",
      color: "onSurface",
    },
  },

  text: {
    displayLarge: {
      fontFamily: "heading",
      fontWeight: "regular",
      fontSize: [9, 10],
      lineHeight: [1.16, 1.12],
      letterSpacing: "-0.25px",
      color: "onSurface",
    },
    displayMedium: {
      fontFamily: "heading",
      fontWeight: "regular",
      fontSize: [8, 9],
      lineHeight: [1.22, 1.16],
      letterSpacing: "0px",
      color: "onSurface",
    },
    displaySmall: {
      fontFamily: "heading",
      fontWeight: "regular",
      fontSize: [6, 8],
      lineHeight: [1.25, 1.22],
      letterSpacing: "0px",
      color: "onSurface",
    },
    headlineLarge: {
      fontFamily: "heading",
      fontWeight: "regular",
      fontSize: [5, 7],
      lineHeight: [1.33, 1.25],
      letterSpacing: "0px",
      color: "onSurface",
    },
    headlineMedium: {
      fontFamily: "heading",
      fontWeight: "regular",
      fontSize: [4, 6],
      lineHeight: [1.27, 1.29],
      letterSpacing: "0px",
      color: "onSurface",
    },
    headlineSmall: {
      fontFamily: "heading",
      fontWeight: "regular",
      fontSize: [3, 5],
      lineHeight: [1.5, 1.33],
      letterSpacing: "0px",
      color: "onSurface",
    },
    titleLarge: {
      fontFamily: "heading",
      fontWeight: "medium",
      fontSize: [4],
      lineHeight: 1.27,
      letterSpacing: "0px",
      color: "onSurface",
    },
    titleMedium: {
      fontFamily: "heading",
      fontWeight: "medium",
      fontSize: [3],
      lineHeight: 1.5,
      letterSpacing: "0.15px",
      color: "onSurface",
    },
    titleSmall: {
      fontFamily: "heading",
      fontWeight: "medium",
      fontSize: [2],
      lineHeight: 1.43,
      letterSpacing: "0.1px",
      color: "onSurface",
    },
    bodyLarge: {
      fontFamily: "body",
      fontWeight: "regular",
      fontSize: [3, 3],
      lineHeight: 1.5,
      letterSpacing: "0.5px",
      color: "onSurface",
    },
    bodyMedium: {
      fontFamily: "body",
      fontWeight: "regular",
      fontSize: [2, 2],
      lineHeight: 1.43,
      letterSpacing: "0.25px",
      color: "onSurface",
    },
    bodySmall: {
      fontFamily: "body",
      fontWeight: "regular",
      fontSize: [1, 1],
      lineHeight: 1.33,
      letterSpacing: "0.4px",
      color: "onSurface",
    },
    labelLarge: {
      fontFamily: "body",
      fontWeight: "medium",
      fontSize: [2, 2],
      lineHeight: 1.43,
      letterSpacing: "0.1px",
      textTransform: "uppercase",
      color: "onSurface",
    },
    labelMedium: {
      fontFamily: "body",
      fontWeight: "medium",
      fontSize: [1, 1],
      lineHeight: 1.33,
      letterSpacing: "0.5px",
      textTransform: "uppercase",
      color: "onSurface",
    },
    labelSmall: {
      fontFamily: "body",
      fontWeight: "medium",
      fontSize: [0, 0],
      lineHeight: 1.45,
      letterSpacing: "0.5px",
      textTransform: "uppercase",
      color: "onSurface",
    },
    buttonLabel: {
      fontFamily: "body",
      fontWeight: "regular",
      fontSize: [1, 1],
      lineHeight: 1.33,
      letterSpacing: "0.4px",
      color: "onSurface",
      p: 24,
      textAlign: "left",
      alignContent: "left",
    },
  },

  styles: {
    root: {
      fontFamily: "body",
      fontWeight: "regular",
    },
    "@global": {
      "@font-face": fontFaces,

    ".appScrollbar": {
  scrollbarWidth: "thin",
  scrollbarColor: `${hexToRgba("#D0C5B4", 0.42)} transparent`,
},
".appScrollbar::-webkit-scrollbar": {
  width: "10px",
  height: "10px",
},
".appScrollbar::-webkit-scrollbar-track": {
  backgroundColor: "transparent",
},
".appScrollbar::-webkit-scrollbar-thumb": {
  backgroundColor: "scrollbarThumb",
  borderRadius: "999px",
  border: "2px solid transparent",
  backgroundClip: "padding-box",
},
".appScrollbar::-webkit-scrollbar-thumb:hover": {
  backgroundColor: "scrollbarThumbHover",
},
".appScrollbar::-webkit-scrollbar-corner": {
  backgroundColor: "transparent",
},
    },
    pre: {
      fontFamily: "monospace",
      overflowX: "auto",
      code: {
        color: "inherit",
      },
    },
    code: {
      fontFamily: "monospace",
      fontSize: "inherit",
    },
    table: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: 0,
    },
    th: {
      textAlign: "left",
      borderBottomStyle: "solid",
    },
    td: {
      textAlign: "left",
      borderBottomStyle: "solid",
    },
    a: {
      color: "primary",
      textDecoration: "none",
      ":visited": {
        color: "primary",
      },
      ":hover": {
        color: "primary",
        textDecoration: "underline",
      },
      ":focus": {
        color: "primary",
      },
    },
    contactList: {
      listStyle: "none",
      padding: 0,
      margin: 0,
    },
    contactListItemHeader: {
      justifyContent: "space-between",
      alignItems: "center",
      display: ["flex", "flex", "flex"],
      pb: [0, 0],
      fontSize: 2,
      p: 0,
      m: 0,
    },
  },

  sizes: {
    container: 1028,
  },

  cards: {
    primary: {
      padding: [3, 4],
      borderRadius: 4,
      background: "surfaceContainerLow",
      color: "onSurface",
    },
    cardList: {
      px: 3,
      py: 4,
      mb: 2,
      color: "onSurface",
    },
    cardListUpdates: {
      px: 2,
      py: 3,
      mb: 1,
      color: "onSurface",
      border: "none",
      background: "surfaceContainer",
    },
    donation: {
      px: "12px",
      py: "16px",
      mb: 1,
      color: "onError",
      border: "none",
      backgroundColor: "error",
    },
    homepage: {
      padding: 0,
      borderRadius: 12,
      boxShadow: "boxShadow1dp",
      background: "surfaceContainer",
      color: "onSurface",
    },
    compact: {
      padding: 1,
      borderRadius: 2,
      border: "1px solid",
      borderColor: "muted",
    },
    timeline: {
      position: "relative",
      margin: "16px 16px 16px 16px",
      backgroundColor: "surfaceContainerLow",
      padding: [3, 4],
      borderRadius: 4,
      boxShadow: "boxShadow1dp",
    },
    roadmap: {
      position: "relative",
      margin: "16px 16px 16px 16px",
      backgroundColor: "surfaceContainerLow",
      padding: [3, 4],
      borderRadius: 4,
      boxShadow: "boxShadow1dp",
    },
    beforeduringaftercards: {
      color: "darkSandstone",
      borderRadius: 4,
      boxShadow: "0 0 8px rgba(0, 0, 0, 0.125)",
      backgroundColor: "surfaceContainerLow",
    },
    warning: {
      border: "0px solid",
      color: "onErrorContainer",
      borderColor: "error",
      borderRadius: 4,
      backgroundColor: "errorContainer",
      fontSize: 0,
    },
    advisory: {
      border: "0px solid",
      color: "onErrorContainer",
      borderColor: "error",
      borderRadius: 4,
      backgroundColor: "errorContainer",
      fontSize: 0,
    },
    no_alerts: {
      borderRadius: 4,
      padding: 3,
      px: 2,
      backgroundColor: "tertiaryContainer",
      color: "onTertiaryContainer",
      "&:hover": {
        backgroundColor: "tertiaryContainerHover",
      },
    },
  },

  buttons: {
    primary: {
      fontFamily: '"Nunito Sans", sans-serif',
      minHeight: "44px",
      minWidth: "44px",
      fontSize: [1, 2],
      borderRadius: "24px",
      color: "onPrimary",
      border: "1px solid",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderColor: "primary",
      background: "primary",
      cursor: "pointer",
      transition: "background-color 0.1s ease",
      transitionProperty: "box-shadow",
      transitionDuration: "0.15s",
      transitionTimingFunction: "ease-in-out",
      boxShadow: "boxShadow1dp",
      "&:hover": {
        boxShadow: "boxShadow3dp",
        background: "primary90",
        borderColor: "primary90",
      },
    },
    secondary: {
      fontFamily: '"Nunito Sans", sans-serif',
      boxShadow: "boxShadow1dp",
      minHeight: "44px",
      minWidth: "44px",
      fontSize: [1, 2],
      borderRadius: "24px",
      border: "1px solid",
      borderColor: "outline",
      background: "none",
      color: "primary",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "background-color 0.3s ease",
      transitionProperty: "box-shadow",
      transitionDuration: "0.1s",
      transitionTimingFunction: "ease-in-out",
      "&:hover": {
        boxShadow: "boxShadow3dp",
        background: "neutral30",
      },
    },
    tertiary: {
      fontFamily: '"Nunito Sans", sans-serif',
      minHeight: "44px",
      minWidth: "44px",
      borderRadius: "24px",
      paddingTop: "8px",
      border: "1px solid red",
      borderColor: "outlineVariant",
      background: "transparent",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: "primary",
      cursor: "pointer",
      transition: "color 0.15s ease",
      transitionProperty: "box-shadow",
      transitionDuration: "0.1s",
      transitionTimingFunction: "ease-in-out",
      "&:hover": {
        boxShadow: "boxShadow3dp",
        backgroundColor: "surfaceContainerHighest",
      },
    },
    elevated: {
      background: "surfaceContainerLow",
      cursor: "pointer",
      color: "onSurface",
      minHeight: "60px",
      boxShadow: "boxShadow1dp",
      fontSize: [1, 2],
      width: "100%",
      textAlign: "left",
      "&:hover": {
        boxShadow: "boxShadow3dp",
        backgroundColor: "surfaceContainer",
      },
    },
    dropdown: {
      p: 0,
      "&:hover": {
        backgroundColor: "surfaceContainerHighest",
        cursor: "pointer",
      },
    },
    text: {
      ml: 2,
      flexShrink: 0,
      fontSize: [0],
      color: "onSurfaceVariant",
      px: 2,
      py: 1,
      whiteSpace: "nowrap",
      alignSelf: "flex-start",
      cursor: "pointer",
      bg: "transparent",
      "&:hover": {
        color: "primary",
      },
    },
    alert: {
      fontFamily: '"Nunito Sans", sans-serif',
      minHeight: "44px",
      minWidth: "44px",
      borderRadius: "24px",
      border: "none",
      background: "transparent",
      color: "onSurface",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "color 0.3s ease",
      transitionProperty: "box-shadow",
      transitionDuration: "0.1s",
      transitionTimingFunction: "ease-in-out",
      "&:hover": {
        boxShadow: "boxShadow3dp",
      },
    },
    scrolling: {
      backgroundColor: "surfaceContainerHighest",
      boxShadow: "boxShadow8dp",
      width: "55px",
      height: "55px",
      borderRadius: "60px",
      "&:hover": {
        cursor: "pointer",
        backgroundColor: "surfaceContainerHigh",
      },
    },
    chip: {
      minHeight: "24px",
      p: 2,
      m: 1,
      mt: 2,
      borderRadius: "8px",
      borderColor: "outlineVariant",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "boxShadow1dp",
      "&:hover": {
        background: "surfaceContainerHigh",
        color: "onSurface",
        cursor: "pointer",
      },
    },
    icon: {
      color: "#000",
      backgroundColor: "sandstone",
      fontSize: 4,
      padding: 0,
      marginTop: 3,
      "&:hover": {
        cursor: "pointer",
        color: "primary",
      },
    },
    back: {
      color: "white",
      backgroundColor: "background",
      fontSize: 4,
      padding: 0,
      marginTop: 2,
      verticalAlign: "top",
      "&:hover": {
        cursor: "pointer",
        color: "primary",
      },
    },
    previousLink: {
      display: "flex",
      flexDirection: "column",
      alignItems: "left",
      textAlign: "left",
      lineHeight: 1.2,
      padding: 3,
      mt: 3,
      width: "100%",
      borderRadius: "16px",
      backgroundColor: "surfaceContainer",
      border: "0px solid",
      borderColor: "surfaceContainerHighest",
      color: "onSurface",
      cursor: "pointer",
      transition: "background-color 0.3s ease",
      transitionProperty: "box-shadow",
      transitionDuration: "0.3s",
      transitionTimingFunction: "ease-in-out",
      "&:hover": {
        backgroundColor: "surfaceContainerHighest",
        textDecoration: "none",
      },
      "& > span:nth-child(1)": {
        fontFamily: "body",
        fontWeight: "regular",
        fontSize: [1, 1],
        lineHeight: 1.33,
        letterSpacing: "0.4px",
        color: "primary",
      },
      "& > span:nth-child(2)": {
        fontFamily: "body",
        fontWeight: "regular",
        fontSize: [1, 1],
        lineHeight: 1.33,
        letterSpacing: "0.4px",
        color: "onSurface",
      },
      "& > span:nth-child(3)": {
        fontSize: 4,
      },
    },
    nextLink: {
      display: "flex",
      flexDirection: "column",
      alignItems: "left",
      alignContent: "center",
      textAlign: "right",
      padding: 3,
      mt: 3,
      width: "100%",
      borderRadius: "16px",
      border: "0px solid",
      color: "onSurface",
      cursor: "pointer",
      borderColor: "surfaceContainer",
      backgroundColor: "surfaceContainer",
      transition: "background-color 0.3s ease",
      transitionProperty: "box-shadow",
      transitionDuration: "0.3s",
      transitionTimingFunction: "ease-in-out",
      "&:hover": {
        backgroundColor: "surfaceContainerHighest",
        textDecoration: "none",
      },
      "& > span:nth-child(1)": {
        fontFamily: "body",
        fontWeight: "regular",
        fontSize: [1, 1],
        lineHeight: 1.33,
        letterSpacing: "0.4px",
        color: "primary",
      },
      "& > span:nth-child(2)": {
        fontFamily: "body",
        fontWeight: "regular",
        fontSize: [1, 1],
        lineHeight: 1.33,
        letterSpacing: "0.4px",
        color: "onSurface",
      },
      "& > span:nth-child(3)": {
        fontSize: 4,
      },
    },
    tabs: {
      marginTop: "20px",
    },
    tabContainer: {
      display: "flex",
      flexWrap: "wrap",
      gap: "0.5rem",
      borderTop: "1px solid #2c2a26",
      borderBottom: "1px solid #2c2a26",
      paddingLeft: "6px",
    },
    tab: {
      padding: "0.5rem 1rem",
      border: "none",
      backgroundColor: "transparent",
      cursor: "pointer",
      outline: "none",
      fontSize: "1rem",
      fontWeight: "500",
      textTransform: "uppercase",
      transition: "color 0.3s",
      listStyle: "none",
      display: "inline",
    },
    activeTab: {
      color: "#f2bf48",
      borderBottom: "2px solid #f2bf48",
    },
    reactTabsTabPanel: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "500px",
      margin: "0 auto",
    },
    reactTabsTabPanelDiv: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "500px",
      margin: "0 auto",
    },
  },
});